import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  IsExpenseLockedDocument,
  type IsExpenseLockedQuery,
  type IsExpenseLockedQueryVariables,
} from "@src/__generated__/graphql";
import {
  GetExpenseUpdatedAtDocument,
  type GetExpenseUpdatedAtQuery,
  type GetExpenseUpdatedAtQueryVariables,
} from "@src/__generated__/urql-graphql";
import { OcrBeta } from "@src/components/modules/expenses/Create/components/OcrBeta";
import { Icon } from "@src/components/ui-kit/Icon";
import { client } from "@src/services/client";
import { can } from "@src/utils/components/permissions";
import { toApiDateTime } from "@src/utils/dates";
import { useStore as useStores } from "@src/utils/hooks";
import { isBefore } from "date-fns";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, useEffect } from "react";
import { useStore } from "../../store";

const HALF_MINUTE = 30000;

export const ExpenseUpdateWhileInactiveBanner = () => {
  const { reload } = useRouter();
  const { expenseUpdatedWhileInactive } = useStore();

  if (!expenseUpdatedWhileInactive) return null;

  return (
    <Box w="fit-content">
      <Alert status="warning">
        <AlertIcon />
        <Box>
          <Text fontSize="lg" fontWeight="medium">
            <Trans>Expense was updated while this window was inactive.</Trans>
          </Text>
          <Text>
            <Trans>Refresh to view changes.</Trans>
          </Text>
        </Box>
        <IconButton
          ml="2"
          aria-label={t`window refresh button`}
          icon={<Icon name="refresh-cw-02" />}
          onClick={reload}
          variant="ghost"
        />
      </Alert>
    </Box>
  );
};

export const Header = observer(function Header() {
  const { reload } = useRouter();
  const store = useStore();
  const { authStore } = useStores();
  const {
    expense,
    lockedDocument,
    isDocumentLocked,
    isDocumentLockedByMe,
    activePredictionFile,
    activePredictionFormFilled,
    predictionFormCheck,
    predictionFormFillInProgress,
  } = store;

  const handleLockDocument = () => {
    if (!expense?.id) return;
    if (!isDocumentLockedByMe && isDocumentLocked) return;
    store.lockDocument(expense.id);
  };

  const handleCheckIfUnlocked = async (
    allowNullLocked = false,
    lockIfUnlocked?: boolean,
  ) => {
    if (!expense?.id) return;
    const { data } = await client
      .query<IsExpenseLockedQuery, IsExpenseLockedQueryVariables>(
        IsExpenseLockedDocument,
        {
          id: expense.id,
        },
      )
      .toPromise();

    if (!data) return;

    runInAction(() => {
      if (data.expense.lockedDocument) {
        store.lockedDocument = data.expense.lockedDocument;
        return;
      }

      if (!allowNullLocked) return;
      store.lockedDocument = null;
    });

    if (
      !lockIfUnlocked ||
      (data.expense.lockedDocument &&
        data.expense.lockedDocument.user.id !== authStore.user?.id)
    )
      return;
    if (store.timeDifference && store.timeDifference.getMinutes() >= 1) return;
    handleLockDocument();
  };

  const getUpdatedAt = async () => {
    if (!expense?.id) return;

    const { data } = await client
      .query<GetExpenseUpdatedAtQuery, GetExpenseUpdatedAtQueryVariables>(
        GetExpenseUpdatedAtDocument,
        { id: expense.id },
      )
      .toPromise();

    if (!data) return;

    runInAction(() => {
      if (!store.expense?.updated_at) return;
      if (isBefore(new Date(expense.updated_at), data.expense.updated_at)) {
        store.expenseUpdatedWhileInactive = true;
        store.expense.updated_at = toApiDateTime(data.expense.updated_at);
      }
    });
  };

  const handleFocus = () => {
    handleCheckIfUnlocked(true, true);
    getUpdatedAt();
  };

  useEffect(() => {
    const checkLockedInterval = setInterval(() => {
      handleCheckIfUnlocked(true, true);
    }, HALF_MINUTE);

    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(checkLockedInterval);
      window.removeEventListener("focus", () => handleFocus);
    };
  }, []);

  return (
    <Fragment>
      {isDocumentLocked && (
        <VStack align="start" mb="6" spacing="3">
          <Alert status="error">
            <AlertIcon />
            <Box w="full">
              <Text fontSize="lg" fontWeight="medium">
                <Trans>The document is being edited by</Trans>&nbsp;
                {lockedDocument?.user.full_name}
              </Text>
              <Text>
                <Trans>Your changes can't be saved.</Trans>
              </Text>
              {store.timeDifference &&
                store.timeDifference.getMinutes() < 3 &&
                store.timeDifference.getMinutes() >= 0 && (
                  <Text>
                    <Trans>Checking again in:</Trans>&nbsp;
                    {store.timeDifference.getMinutes()}:
                    {store.timeDifference.getSeconds() < 10 ? "0" : ""}
                    {store.timeDifference.getSeconds()}
                  </Text>
                )}
            </Box>
            <IconButton
              ml="2"
              aria-label={t`window refresh button`}
              icon={<Icon name="refresh-cw-02" />}
              onClick={reload}
              variant="ghost"
            />
          </Alert>
          <HStack spacing="2">
            <Button
              colorScheme="red"
              onClick={async () => {
                runInAction(() => {
                  store.lockedDocument = null;
                });

                if (!expense?.id) return;

                store.lockDocument(expense.id);
              }}
              variant="outline"
            >
              <Trans>Edit anyway</Trans>
            </Button>
            <Text color="red.600">
              <Trans>This will result in the loss of</Trans>&nbsp;
              {lockedDocument?.user.full_name}&nbsp;
              <Trans>changes</Trans>
            </Text>
          </HStack>
        </VStack>
      )}
      <Box mb={store.expenseUpdatedWhileInactive ? "6" : undefined}>
        <ExpenseUpdateWhileInactiveBanner />
      </Box>
      <Flex
        justify="space-between"
        direction={{ base: "column", md: "row" }}
        gap="3"
        mb="6"
      >
        <HStack align="baseline" spacing="2">
          <VStack align="start">
            <Heading size="md">
              {expense?.id ? (
                <Trans>Edit expense</Trans>
              ) : (
                <Trans>New expense</Trans>
              )}
            </Heading>
          </VStack>
        </HStack>

        <HStack align="center" spacing="2">
          <Flex direction={{ base: "column", md: "row" }} gap="3">
            {!predictionFormCheck &&
              !!activePredictionFile &&
              !activePredictionFormFilled && (
                <Button
                  isDisabled={predictionFormFillInProgress}
                  onClick={() => store.predictionFillForm()}
                  size="sm"
                  variant="subtle"
                >
                  <Trans>Extract data from document</Trans>
                </Button>
              )}
            <Checkbox
              isChecked={predictionFormCheck}
              onChange={({ target }) => {
                store.predictionFormCheck = target.checked;
              }}
            >
              <Text fontSize="sm">
                <Trans>Automatically extract document data with</Trans>
                &nbsp;
                <OcrBeta />
              </Text>
            </Checkbox>
          </Flex>

          {can("expense_modelLock") && (
            <Button
              colorScheme="orange"
              isLoading={store.isLockExpenseToggling}
              leftIcon={
                store.hasModelLock ? (
                  <Icon name="lock-unlocked-01" />
                ) : (
                  <Icon name="lock-01" />
                )
              }
              onClick={() =>
                store.hasModelLock ? store.unlockExpense() : store.lockExpense()
              }
              variant="outline"
            >
              {store.hasModelLock ? <Trans>Unlock</Trans> : <Trans>Lock</Trans>}
            </Button>
          )}

          {store.canDeleteExpense && (
            <Button
              colorScheme="red"
              isDisabled={
                store.isDocumentLocked ||
                store.expenseUpdatedWhileInactive ||
                store.hasModelLock
              }
              isLoading={store.isDeleting}
              onClick={store.deleteExpenseModalState.onOpen}
              variant="outline"
            >
              <Trans>Delete</Trans>
            </Button>
          )}
        </HStack>
      </Flex>
    </Fragment>
  );
});
